import React from 'react'

import { IconButton, Typography, Theme } from '@material-ui/core'
import {
    BaseWorkorder,
    Service,
    SERVICE_CATEGORY_LABELS,
    SERVICE_CATEGORY_ORDER,
    ServiceCategoryCode,
    TemplateService,
    ezScheduleDetails,
} from '../../../models'
import { Container } from '../../../components'
import { usePopover } from '../../../hooks/usePopover'
import { SelectorPopover } from '../../../components/TemplateScheduleGantt/Components/SelectorPopover/SelectorPopover'

import { ServiceTableRow } from './ServiceTableRow'
import { formatTotals } from '../services'
import AddIcon from '@material-ui/icons/Add'
import { getTotalPriceExpense } from '../../../models/WorkOrder/services'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import {
    HeaderValue,
    IconValue,
    MoneyValue,
    TextValue,
    TotalsMoneyValue,
    TotalsTextValue,
} from '../styles'
import {
    AddTemplateServiceParams,
    UpdateTemplateServiceParams,
    DeleteTemplateServiceParams,
} from '../../../hooks/useEzSchedule/types'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    serviceList: Service[]
    workorders: BaseWorkorder[]
    portfolio?: boolean
    theme: Theme
    ezSchedule: ezScheduleDetails
    addTemplateService: (params: AddTemplateServiceParams) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
}

export const ServiceTable = (props: Props) => {
    const {
        theme,
        serviceList,
        workorders,
        portfolio,
        ezSchedule,
        addTemplateService,
        updateTemplateService,
        deleteTemplateService,
    } = props

    const popoverState = usePopover()
    const templateServices = ezSchedule.phases[0]?.template_services ?? []

    const templateServiceIds = templateServices.map((ts) => ts.service.id) || []

    const filteredServiceList = React.useMemo(() => {
        const workorderServiceIds = new Set(
            workorders.map((wo) => wo.service_id).filter(Boolean),
        )
        return serviceList.filter(
            (service) =>
                workorderServiceIds.has(service.id) &&
                !templateServiceIds.includes(service.id),
        )
    }, [serviceList, workorders, templateServiceIds])

    const forecastedServiceTotals = React.useMemo(() => {
        const totals: { [serviceId: number]: number } = {}
        workorders.forEach((wo) => {
            if (wo.service_id && templateServiceIds.includes(wo.service_id)) {
                totals[wo.service_id] =
                    (totals[wo.service_id] || 0) + getTotalPriceExpense(wo)
            }
        })
        return totals
    }, [workorders, templateServices])

    const invoicedServiceTotals = React.useMemo(() => {
        const totals: { [serviceId: number]: number } = {}
        workorders.forEach((wo) => {
            if (
                wo.service_id &&
                templateServiceIds.includes(wo.service_id) &&
                wo.invoiced_price !== undefined &&
                wo.invoiced_price !== null
            ) {
                totals[wo.service_id] =
                    (totals[wo.service_id] || 0) + wo.invoiced_price
            }
        })
        return totals
    }, [workorders, templateServiceIds])

    const totalForecast = React.useMemo(
        () =>
            Object.values(forecastedServiceTotals).reduce(
                (acc, val) => acc + val,
                0,
            ),
        [forecastedServiceTotals],
    )

    const totalInvoiced = React.useMemo(
        () =>
            Object.values(invoicedServiceTotals).reduce(
                (acc, val) => acc + val,
                0,
            ),
        [invoicedServiceTotals],
    )

    const totalBudget = React.useMemo(
        () => templateServices.reduce((acc, ts) => acc + Number(ts.budget), 0),
        [templateServices],
    )

    const totalVariance = React.useMemo(
        () => (totalBudget || 0) - totalForecast,
        [totalBudget, totalForecast],
    )

    const getCategoryCode = (service: Service): ServiceCategoryCode | null => {
        return (
            service.service_category ??
            serviceList.find((s) => s.id === service.id)?.service_category ??
            null
        )
    }

    const categoriesWithScheduledServices = React.useMemo(() => {
        const present = new Set<ServiceCategoryCode>()
        templateServices.forEach((ts) => {
            const code = getCategoryCode(ts.service)
            if (code) present.add(code)
        })
        return SERVICE_CATEGORY_ORDER.filter((code) => present.has(code))
    }, [templateServices, serviceList])

    const [openCategories, setOpenCategories] = React.useState<
        Set<ServiceCategoryCode>
    >(new Set())

    const toggleCategory = (categoryCode: ServiceCategoryCode) => {
        setOpenCategories((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(categoryCode)) {
                newSet.delete(categoryCode)
            } else {
                newSet.add(categoryCode)
            }
            return newSet
        })
    }

    const calculateCategoryTotals = (categoryCode: ServiceCategoryCode) => {
        let budget = 0
        let forecast = 0
        let invoiced = 0

        templateServices.forEach((ts) => {
            if (getCategoryCode(ts.service) !== categoryCode) return
            budget += Number(ts.budget)
            forecast += forecastedServiceTotals[ts.service.id] || 0
            invoiced += invoicedServiceTotals[ts.service.id] || 0
        })

        return { budget, forecast, variance: budget - forecast, invoiced }
    }

    return (
        <Box sx={{ flexGrow: 1, padding: 2 }}>
            <Container
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    minHeight: 40,
                }}
            >
                {/* Title */}
                <Container
                    style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <Typography
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                        }}
                    >
                        Services
                    </Typography>
                </Container>
            </Container>
            <Grid container spacing={0}>
                {/* Header Row */}
                <Grid item xs={0.5}>
                    <HeaderValue>&nbsp;</HeaderValue>
                </Grid>
                <Grid item xs={3.5}>
                    <HeaderValue
                        style={{
                            justifyContent: 'flex-start',
                        }}
                    >
                        Service
                    </HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Budget</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Forecast</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Variance</HeaderValue>
                </Grid>
                <Grid item xs={2}>
                    <HeaderValue>Invoiced</HeaderValue>
                </Grid>
                {/* Render Categories */}
                {categoriesWithScheduledServices.map((categoryCode) => {
                    const {
                        budget,
                        forecast,
                        variance,
                        invoiced,
                    } = calculateCategoryTotals(categoryCode)
                    return (
                        <React.Fragment key={`CATEGORY-${categoryCode}`}>
                            <Grid container item xs={12}>
                                <Grid item xs={0.5}>
                                    <IconValue>
                                        <IconButton
                                            onClick={() =>
                                                toggleCategory(categoryCode)
                                            }
                                        >
                                            {openCategories.has(
                                                categoryCode,
                                            ) ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )}
                                        </IconButton>
                                    </IconValue>
                                </Grid>
                                <Grid item xs={3.5}>
                                    <TextValue>
                                        {SERVICE_CATEGORY_LABELS[categoryCode]}
                                    </TextValue>
                                </Grid>
                                <Grid item xs={2}>
                                    <MoneyValue>
                                        {formatTotals(budget)}
                                    </MoneyValue>
                                </Grid>
                                <Grid item xs={2}>
                                    <MoneyValue>
                                        {formatTotals(forecast)}
                                    </MoneyValue>
                                </Grid>
                                <Grid item xs={2}>
                                    <MoneyValue>
                                        {formatTotals(variance)}
                                    </MoneyValue>
                                </Grid>
                                <Grid item xs={2}>
                                    <MoneyValue>
                                        {formatTotals(invoiced)}
                                    </MoneyValue>
                                </Grid>
                            </Grid>
                            {openCategories.has(categoryCode) && (
                                <>
                                    {templateServices
                                        .filter(
                                            (ts) =>
                                                getCategoryCode(ts.service) ===
                                                categoryCode,
                                        )
                                        .map((ts) => (
                                            <ServiceTableRow
                                                {...props}
                                                key={`TEMPLATE-SERVICE-${ts.id}`}
                                                templateService={ts}
                                                forecastedServiceTotal={
                                                    forecastedServiceTotals[
                                                        ts.service.id
                                                    ] || 0
                                                }
                                                invoicedServiceTotal={
                                                    invoicedServiceTotals[
                                                        ts.service.id
                                                    ] || 0
                                                }
                                            />
                                        ))}
                                </>
                            )}
                        </React.Fragment>
                    )
                })}
                {/* Render Uncategorised Template Services */}
                {templateServices
                    .filter((ts) => getCategoryCode(ts.service) == null)
                    .map((ts) => (
                        <ServiceTableRow
                            {...props}
                            key={`TEMPLATE-SERVICE-${ts.id}`}
                            templateService={ts}
                            forecastedServiceTotal={
                                forecastedServiceTotals[ts.service.id] || 0
                            }
                            invoicedServiceTotal={
                                invoicedServiceTotals[ts.service.id] || 0
                            }
                            updateTemplateService={updateTemplateService}
                            deleteTemplateService={deleteTemplateService}
                        />
                    ))}
                {/* Add Service Row */}
                <Grid item xs={0.5}>
                    <IconValue>
                        <IconButton onClick={popoverState.handleOpen}>
                            <AddIcon />
                        </IconButton>
                    </IconValue>
                    <SelectorPopover
                        {...props}
                        popoverState={popoverState}
                        data={filteredServiceList}
                        label="Select Service"
                        onSubmit={(selectedId) => {
                            addTemplateService({
                                serviceId: selectedId,
                                serviceName:
                                    filteredServiceList.find(
                                        (service) => service.id === selectedId,
                                    )?.name ?? '',
                                scheduleId: ezSchedule.id,
                                portfolio: portfolio,
                            })
                        }}
                    />
                </Grid>
                <Grid item xs={3.5}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                <Grid item xs={2}>
                    <TextValue></TextValue>
                </Grid>
                {/* Totals Row */}
                <Grid item xs={0.5}>
                    <TotalsTextValue>&nbsp;</TotalsTextValue>
                </Grid>
                <Grid item xs={3.5}>
                    <TotalsTextValue>Totals</TotalsTextValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(totalBudget)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(totalForecast)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(totalVariance)}
                    </TotalsMoneyValue>
                </Grid>
                <Grid item xs={2}>
                    <TotalsMoneyValue>
                        {formatTotals(totalInvoiced)}
                    </TotalsMoneyValue>
                </Grid>
            </Grid>
        </Box>
    )
}
